/**
 * Custom legend component for charts with limited visible items and scrollable overflow
 */

import React, { useState, useRef, useEffect } from 'react';

interface LegendItem {
  label: string;
  color: string;
  value: number;
  percentage?: number;
}

interface CustomLegendProps {
  items: LegendItem[];
  maxVisible?: number;
  theme: 'light' | 'dark';
  showPercentage?: boolean;
  height?: number;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  items,
  maxVisible = 5,
  theme,
  showPercentage = true,
  height = 256
}) => {
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const secondaryTextColor = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding errors
      const hasScroll = scrollHeight > clientHeight;

      setShowTopShadow(!isAtTop && hasScroll);
      setShowBottomShadow(!isAtBottom && hasScroll);
    };

    // Check initial state
    checkScroll();

    // Add scroll event listener
    container.addEventListener('scroll', checkScroll);
    
    // Also check on resize
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [items, height]);

  const shadowGradient = theme === 'dark' 
    ? 'from-gray-800 via-gray-800/80 to-transparent'
    : 'from-white via-white/80 to-transparent';

  return (
    <div className="w-full relative" style={{ height: `${height}px` }}>
      {/* Top shadow */}
      {showTopShadow && (
        <div 
          className={`absolute top-0 left-0 right-0 h-8 pointer-events-none z-10 bg-gradient-to-b ${shadowGradient}`}
        />
      )}
      
      {/* Scrollable container */}
      <div 
        ref={scrollContainerRef}
        className="space-y-2 overflow-y-auto scrollbar-thin h-full"
      >
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className={`flex-1 ${textColor}`}>{item.label}</span>
            {showPercentage && item.percentage !== undefined && (
              <span className={secondaryTextColor}>
                {item.percentage.toFixed(1)}%
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Bottom shadow */}
      {showBottomShadow && (
        <div 
          className={`absolute bottom-0 left-0 right-0 h-8 pointer-events-none z-10 bg-gradient-to-t ${shadowGradient}`}
        />
      )}
    </div>
  );
};

export default CustomLegend;

